import { createSlice } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';
import { api } from 'libs';
import serializeRequestError from 'state/utils/serialize-request-error';
import { filterSet } from './filters';
import type { AppDispatch, AppGetState } from './index';

const INITIAL_META = {
  currentPage: 1,
  hasMore: false,
  perPage: null as number | null,
  total: null as number | null,
};

const lendersByProductsSlice = createSlice({
  name: 'lenderspotlight/lendersByProducts',
  initialState: {
    error: null,
    lendersByProductsResults: [] as any[],
    isLoading: false,
    meta: INITIAL_META,
  },
  reducers: {
    fetchStarted: (state) => {
      state.error = null;
      state.isLoading = true;
    },
    fetchFailed: (state, action) => {
      state.error = action.payload ?? null;
      state.isLoading = false;
      state.lendersByProductsResults = [];
      state.meta = INITIAL_META;
    },
    fetchMoreFailed: (state) => {
      state.isLoading = false;
    },
    fetchSucceeded: (state, action) => {
      state.error = null;
      state.isLoading = false;
      state.lendersByProductsResults = [...action.payload.lendersByProducts];
      state.meta = action.payload.meta;
    },
    fetchMoreSucceeded: (state, action) => {
      state.error = null;
      state.isLoading = false;
      state.lendersByProductsResults = [...state.lendersByProductsResults, ...action.payload.lendersByProducts];
      state.meta = action.payload.meta;
    },
    resultsCleared: (state) => {
      state.error = null;
      state.lendersByProductsResults = [];
      state.meta = INITIAL_META;
    },
    lenderPreferredUpdated: (state, action) => {
      const { lenderId, isPreferred } = action.payload;
      state.lendersByProductsResults = state.lendersByProductsResults.map((lender) =>
        lender.id === lenderId ? { ...lender, is_preferred: isPreferred } : lender
      );
    },
  },
});

export default lendersByProductsSlice.reducer;

const {
  fetchFailed,
  fetchMoreFailed,
  fetchMoreSucceeded,
  fetchStarted,
  fetchSucceeded,
  lenderPreferredUpdated,
  resultsCleared,
} = lendersByProductsSlice.actions;

const getMeta = (headers: AxiosResponse['headers'] | null = null) => {
  return headers === null
    ? {}
    : {
        currentPage: parseInt(headers['x-current-page'], 10),
        hasMore: headers['x-has-more'] === 'true' || false,
        perPage: parseInt(headers['x-per-page'], 10),
        total: parseInt(headers['x-total-count'], 10),
      };
};

const fetchSuccess = (response: AxiosResponse) =>
  fetchSucceeded({
    lendersByProducts: response.data,
    meta: getMeta(response.headers),
  });

const fetchMoreSuccess = (response: AxiosResponse) =>
  fetchMoreSucceeded({
    lendersByProducts: response.data,
    meta: getMeta(response.headers),
  });

const fetchError = (error: unknown) => fetchFailed(serializeRequestError(error));
const fetchMoreError = () => fetchMoreFailed();

const fetch =
  (params = {}) =>
  async (dispatch: AppDispatch) => {
    dispatch(fetchStarted());
    dispatch(filterSet(params));

    try {
      const response = await api.get('lenders/products', { params });
      return dispatch(fetchSuccess(response));
    } catch (error) {
      return dispatch(fetchError(error));
    }
  };

const fetchMore = () => async (dispatch: AppDispatch, getState: AppGetState) => {
  dispatch(fetchStarted());

  const params = {
    ...getState().filters.options,
    page: getState().lendersByProducts.meta.currentPage + 1,
  };

  try {
    const response = await api.get('lenders/products', { params });
    return dispatch(fetchMoreSuccess(response));
  } catch (_) {
    return dispatch(fetchMoreError());
  }
};

const clearResults = () => resultsCleared();

const updateLenderPreferred = (lenderId: number, isPreferred: boolean) =>
  lenderPreferredUpdated({ lenderId, isPreferred });

export const actions = {
  clearResults,
  fetch,
  fetchMore,
  updateLenderPreferred,
};
